import { Phone, Building2, Shield, CheckCircle, ClipboardCheck, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const SafetyAudit = () => {
  const auditFor = [
    {
      icon: Building2,
      title: 'Apartment Complexes',
      description: 'Comprehensive safety assessment for residential communities and gated societies.',
    },
    {
      icon: Shield,
      title: 'Corporate Campuses',
      description: 'Professional snake risk evaluation for office buildings and industrial areas.',
    },
  ];

  const auditIncludes = [
    'Property inspection for snake entry points',
    'Assessment of surrounding environment',
    'Identification of potential snake habitats',
    'Review of vegetation and landscaping',
    'Drainage and water body evaluation',
    'Risk level classification',
    'Customized prevention recommendations',
    'Staff training on emergency procedures',
  ];

  const benefits = [
    'Proactive risk identification before incidents occur',
    'Detailed written report with findings',
    'Practical prevention measures',
    'Peace of mind for residents/employees',
    'Compliance with safety standards',
    'Reduced emergency call-outs',
  ];

  const auditProcess = [
    {
      step: '1',
      title: 'Initial Consultation',
      description: 'Discuss your property specifics and concerns',
    },
    {
      step: '2',
      title: 'Site Inspection',
      description: 'Thorough on-site assessment of the entire property',
    },
    {
      step: '3',
      title: 'Analysis & Report',
      description: 'Detailed findings and risk assessment documentation',
    },
    {
      step: '4',
      title: 'Recommendations',
      description: 'Actionable prevention strategies and follow-up support',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Shield className="inline h-4 w-4 mr-1" />
              Prevention & Assessment
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Snake Safety Audit
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Professional safety assessments for apartments and corporate properties. Identify risks before they become emergencies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="tel:+918310755854">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Phone className="mr-2 h-5 w-5" />
                  Alhan: +91 8310755854
                </Button>
              </a>
              <a href="tel:+919845080903">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Phone className="mr-2 h-5 w-5" />
                  Mohan: +91 9845080903
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Who Needs a Safety Audit?</h2>
              <p className="text-gray-600">Protect your property and residents with professional assessment</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {auditFor.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow border-emerald-100">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-emerald-600" />
                      </div>
                      <h3 className="text-2xl font-semibold mb-3 text-gray-900">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  <ClipboardCheck className="inline h-8 w-8 text-emerald-600 mr-2" />
                  What's Included in the Audit?
                </h2>
                <p className="text-gray-600 mb-6">
                  Our comprehensive safety audit covers all aspects of snake risk assessment:
                </p>
                <ul className="space-y-3">
                  {auditIncludes.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  <FileText className="inline h-8 w-8 text-emerald-600 mr-2" />
                  Benefits of Safety Audit
                </h2>
                <p className="text-gray-600 mb-6">
                  Proactive assessment helps prevent emergencies and ensures resident safety:
                </p>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mt-6">
                  <h3 className="font-semibold text-emerald-900 mb-2">Professional Reporting</h3>
                  <p className="text-emerald-800 text-sm">
                    You'll receive a detailed written report with photographs, risk assessment, and specific recommendations tailored to your property.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audit Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Audit Process</h2>
              <p className="text-gray-600">Simple and thorough assessment procedure</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {auditProcess.map((item, index) => (
                <Card key={index} className="text-center border-emerald-100">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-emerald-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose Our Safety Audit?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-emerald-100">
                <CardContent className="p-6">
                  <Shield className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Expert Assessment</h3>
                  <p className="text-sm text-gray-600">Years of field experience in snake behavior and habitat</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-100">
                <CardContent className="p-6">
                  <FileText className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Detailed Reports</h3>
                  <p className="text-sm text-gray-600">Comprehensive documentation with clear recommendations</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-100">
                <CardContent className="p-6">
                  <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Actionable Solutions</h3>
                  <p className="text-sm text-gray-600">Practical prevention measures you can implement</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Protect Your Property Today
          </h2>
          <p className="text-xl mb-8 text-emerald-50">
            Schedule a professional snake safety audit for your apartment or corporate campus
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:+918310755854">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Alhan: +91 8310755854
              </Button>
            </a>
            <a href="tel:+919845080903">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Mohan: +91 9845080903
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SafetyAudit;